import type { FileFormatFormFieldErrors } from '@/components/file-formats/file-format-form.types';
import { formatErrorMessage } from '@/utils/error-formatting';

// Subset of FileFormatFormFieldErrors keys the API-error mapper can target
// today. Pinning this with `Extract` (rather than a hand-rolled `'code' |
// 'form'` literal) keeps the mapper honest if the canonical form-error
// type ever drops one of those slots, and lets a future rule target a new
// field with a one-character widening here.
type MappedErrorField = Extract<keyof FileFormatFormFieldErrors, 'code' | 'form'>;

interface MappedFileFormatError {
  field: MappedErrorField;
  message: string;
}

// Shown for any 5xx that surfaces the static fallback emitted by
// file-format.controller.ts respondError(). The user can't act on the
// underlying cause, so the copy is retry-oriented and reassures them
// that the input wasn't the problem.
const SERVER_ERROR_MESSAGE =
  'Something went wrong on our end. Please try again, and contact support if it keeps happening.';

// Connectivity failures originating below the API: keycloak-auth's JSON
// parse fallback ("Network error occurred"), Chrome/Edge's native fetch
// ("Failed to fetch"), Firefox ("NetworkError when attempting to fetch
// resource."), and Safari ("Load failed"). Anchored alternation prevents
// "Failed to fetch" from swallowing the longer 5xx fallbacks below.
const NETWORK_ERROR_PATTERN =
  /^(network error occurred|failed to fetch|networkerror when attempting to fetch resource\.?|load failed)$/i;

const NETWORK_ERROR_MESSAGE = "We couldn't reach the server. Check your connection and try again.";

// 5xx fallbacks emitted by file-format.controller.ts respondError(). The
// "Failed to (fetch|create|update|delete) file format[s]?" string family
// is the only message on that path, so matching it is a reliable proxy
// for "this came from the server's catch-all branch".
const SERVER_ERROR_PATTERN = /^failed to (fetch|create|update|delete|restore) file format/i;

interface ErrorRule {
  fingerprint: RegExp;
  field: MappedErrorField;
  /**
   * Optional rewrite. When absent, the original message is shown verbatim
   * (right for actionable 4xx like 409 conflicts, where the backend
   * phrasing is already clear). When present, the backend's wording is
   * masked with copy the user can act on -- used for 5xx fallbacks and
   * network failures, where the original is uninformative or alarming.
   */
  rewrite?: string;
}

// Order matters: first match wins, so place broad patterns last. Each
// fingerprint is anchored where overlap with another rule is possible.
const ERROR_RULES: ReadonlyArray<ErrorRule> = [
  // 409 Conflict: surface inline on the offending input -- the user can
  // resolve it by editing the code.
  { fingerprint: /already exists for this scope/i, field: 'code' },
  // Pre-API connectivity failure (offline, DNS, CORS, browser abort).
  { fingerprint: NETWORK_ERROR_PATTERN, field: 'form', rewrite: NETWORK_ERROR_MESSAGE },
  // Server's catch-all 500 branch.
  { fingerprint: SERVER_ERROR_PATTERN, field: 'form', rewrite: SERVER_ERROR_MESSAGE },
];

// Defensive cap on the verbatim fall-through path: today the backend only
// emits short, sanitized messages, but a future leak (e.g. a Zod issue
// list serialized into the message) shouldn't blow out the form-level
// error slot or scroll the modal off-screen.
const MAX_USER_MESSAGE_LENGTH = 280;
const clampUserMessage = (s: string): string =>
  s.length > MAX_USER_MESSAGE_LENGTH ? `${s.slice(0, MAX_USER_MESSAGE_LENGTH - 1)}…` : s;

export const mapFileFormatApiError = (error: unknown): MappedFileFormatError => {
  const raw = formatErrorMessage(error);
  const matched = ERROR_RULES.find((rule) => rule.fingerprint.test(raw));
  return {
    field: matched?.field ?? 'form',
    message: matched?.rewrite ?? clampUserMessage(raw),
  };
};
